import { X, ArrowUpRight, Mail, Award } from "lucide-react";
import { motion } from "framer-motion";
import { KIT_PORTFOLIO, MARIO_SECTION, ZAMBIASI_SECTION, EDUARDO_SECTION } from "@/data/about";

const ModalShell = ({ onClose, testid, children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md overflow-y-auto"
    onClick={onClose}
    data-testid={testid}
  >
    <button
      className="fixed top-6 right-6 text-slate-300 hover:text-white p-2 z-10"
      onClick={onClose}
      data-testid={`${testid}-close`}
      aria-label="Close"
    >
      <X size={28} />
    </button>
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 24, opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-5xl mx-auto px-6 py-16"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </motion.div>
  </motion.div>
);

const ModalHead = ({ name, role, bio }) => (
  <>
    <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-4">Portfolio</p>
    <h2 className="f-display text-4xl md:text-6xl font-black tracking-tight">{name}</h2>
    <p className="mt-2 f-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{role}</p>
    <p className="mt-6 text-slate-400 leading-relaxed max-w-2xl">{bio}</p>
  </>
);

const WorkGrid = ({ works, testidPrefix, source }) => (
  <>
    <p className="mt-12 mb-6 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
      Selected work · {source}
    </p>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {works.map((w, i) => (
        <a
          key={w.title}
          href={w.url || `https://vimeo.com/${w.vimeo}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testidPrefix}-work-${i}`}
          className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-red-600/50 transition-colors duration-500"
        >
          <div className="aspect-[4/3] overflow-hidden bg-[#101114]">
            <img
              src={w.thumb}
              alt={w.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            {w.cat && (
              <span className="block f-mono text-[8px] uppercase tracking-[0.2em] text-red-400 mb-1">{w.cat}</span>
            )}
            <div className="flex items-end justify-between gap-2">
              <span className="f-display text-xs md:text-sm font-bold tracking-tight leading-tight">{w.title}</span>
              <ArrowUpRight size={14} className="text-slate-400 group-hover:text-red-500 transition-colors shrink-0" />
            </div>
          </div>
        </a>
      ))}
    </div>
  </>
);

export const KitModal = ({ onClose, lang }) => {
  const t = KIT_PORTFOLIO;
  return (
    <ModalShell onClose={onClose} testid="portfolio-modal">
      <ModalHead name="Kit Lynch Robinson" role="Director (UK)" bio={t.bio[lang]} />
      <WorkGrid works={t.works} testidPrefix="portfolio" source={t.source} />
    </ModalShell>
  );
};

export const MarioModal = ({ onClose, lang }) => {
  const t = MARIO_SECTION;
  return (
    <ModalShell onClose={onClose} testid="mario-modal">
      <ModalHead name="Mario Tagliabue" role="Tabletop Director · DoP" bio={t.bio[lang]} />
      <div className="mt-10">
        <p className="f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">{t.showreel.title[lang]}</p>
        <div className="aspect-video rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="mario-showreel">
          <video src={t.showreel.url} controls preload="metadata" playsInline className="w-full h-full" />
        </div>
      </div>
      <p className="mt-12 mb-4 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {lang === "it" ? "Specializzazioni" : "Specialties"}
      </p>
      <div className="flex flex-wrap gap-2" data-testid="mario-specialties">
        {t.specialties[lang].map((s) => (
          <span key={s} className="f-mono text-[10px] uppercase tracking-[0.12em] bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5 text-slate-300">
            {s}
          </span>
        ))}
      </div>
      <p className="mt-12 mb-4 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {lang === "it" ? "Brand diretti" : "Brands directed"}
      </p>
      <div className="flex flex-wrap gap-x-8 gap-y-3" data-testid="mario-brands">
        {t.brands.map((b) => (
          <span key={b} className="f-display text-base md:text-lg font-extrabold tracking-tight text-slate-500 hover:text-slate-200 transition-colors">
            {b}
          </span>
        ))}
      </div>
      <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-6">
        <a href={`mailto:${t.email}`} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-red-500 transition-colors" data-testid="mario-email">
          <Mail size={15} className="text-red-500" />
          {t.email}
        </a>
        <a href={t.site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-red-500 transition-colors" data-testid="mario-site">
          <ArrowUpRight size={15} className="text-red-500" />
          mariotagliabue.com
        </a>
      </div>
    </ModalShell>
  );
};

export const ZambiasiModal = ({ onClose, lang }) => {
  const t = ZAMBIASI_SECTION;
  return (
    <ModalShell onClose={onClose} testid="zambiasi-modal">
      <ModalHead name="Massimo Zambiasi" role={t.role} bio={t.bio[lang]} />
      <p className="mt-10 mb-4 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {lang === "it" ? "Specializzazioni" : "Specialties"}
      </p>
      <div className="flex flex-wrap gap-2" data-testid="zambiasi-specialties">
        {t.specialties[lang].map((s) => (
          <span key={s} className="f-mono text-[10px] uppercase tracking-[0.12em] bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5 text-slate-300">
            {s}
          </span>
        ))}
      </div>
      <WorkGrid works={t.works} testidPrefix="zambiasi" source="massimozambiasi.it" />
      <div className="mt-12 pt-8 border-t border-white/10">
        <a href={t.site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-red-500 transition-colors" data-testid="zambiasi-site">
          <ArrowUpRight size={15} className="text-red-500" />
          massimozambiasi.it
        </a>
      </div>
    </ModalShell>
  );
};

export const EduardoModal = ({ onClose, lang }) => {
  const t = EDUARDO_SECTION;
  return (
    <ModalShell onClose={onClose} testid="eduardo-modal">
      <ModalHead name="Eduardo Vieitez" role={t.role[lang]} bio={t.bio[lang]} />
      <div className="mt-6 inline-flex items-center gap-2 border border-red-600/40 rounded-full px-4 py-2" data-testid="eduardo-awards">
        <Award size={14} className="text-red-500" />
        <span className="f-mono text-[10px] uppercase tracking-[0.2em] text-red-400">{t.awards[lang]}</span>
      </div>

      <div className="mt-10">
        <p className="f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">{t.featured.title}</p>
        <a
          href={`https://vimeo.com/${t.featured.vimeo}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="eduardo-featured"
          className="group relative block aspect-video rounded-lg overflow-hidden border border-white/10 hover:border-red-600/50 transition-colors duration-500"
        >
          <img
            src="https://i.vimeocdn.com/video/1732451485-b9342339b9266143dc98268e91f0178c08b89a8d6b8b7aa30f45923c4dfbb9d6-d?mw=1280&q=85"
            alt={t.featured.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <ArrowUpRight size={20} className="text-white" />
            </span>
          </span>
        </a>
      </div>

      <WorkGrid works={t.works} testidPrefix="eduardo" source="vimeo.com/eduardovieitez" />

      <p className="mt-12 mb-4 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {lang === "it" ? "Brand diretti" : "Brands directed"}
      </p>
      <div className="flex flex-wrap gap-x-8 gap-y-3" data-testid="eduardo-brands">
        {t.brands.map((b) => (
          <span key={b} className="f-display text-base md:text-lg font-extrabold tracking-tight text-slate-500 hover:text-slate-200 transition-colors">
            {b}
          </span>
        ))}
      </div>
      <p className="mt-8 f-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
        {lang === "it" ? "Agenzie" : "Agencies"}: {t.agencies.join(" · ")}
      </p>

      <div className="mt-12 pt-8 border-t border-white/10">
        <a href={t.site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-red-500 transition-colors" data-testid="eduardo-site">
          <ArrowUpRight size={15} className="text-red-500" />
          eduardovieitez.es
        </a>
      </div>
    </ModalShell>
  );
};
